import * as _ from "lodash";
import { IdsAttribute, IdsFilterSpec, IdsMetric, IdsNode, IdsRow, IMetricDataSet } from "./PA.MetricDataSet.IF";
import { NumberUtils } from "../PA.Core/Basic/Numbers";
import { DateUtils } from "../PA.Core/Basic/Dates";
import { ConstantNode, FunctionNode, MathNode, SymbolNode } from "mathjs";
import { IMessage, MessageType } from "../PA.Core/PA.Core.IF.Basic"

export interface IMetricFunctionSpec {
    name: string
    metricArgIdxs: number[],
    bFilterScope: boolean
}

export interface IDataSetSymbolTypes {
    attributes: string[],
    metrics: string[],
    unknown: string[]
}

export interface IDataSetSummary {
    name: string
    dataProvider: {
        name: string
        dataObject?: string
    },
    status: {
        loaded: boolean
        dataLoadingPaused: boolean
        rowCountThreshold: number

        structureLoaded: {
            attributes: string[]
            metrics: string[]
            filters: IDataSetFilterSummary

            clientSideRowCount: number
            filteredRowCount: number

        }

        structureQueued: {
            attributes: string[]
            metrics: string[]
            filters: IDataSetFilterSummary
        },

    },

    schema: {
        attributes: IdsAttribute[]
        metrics: IdsMetric[]
        hiddenMetrics: IdsMetric[]
    }
}

export interface IDataSetFilterSummary {
    filterCount: number
    clientSideCount?: number
    clientSideFilterIds?: string[],

    serverSideCount?: number
    serverSideFilterIds?: string[]

    staticCount?: number
    staticFilterIds?: string[]
}

// Known PA Functions
//  VisualAvg(MetricId: string, WeightingMetricId?: string, bPositiveOnly?: boolean): number
// 	VisualMin(MetricId: string, bPositiveOnly?: boolean): number
// 	VisualMax(MetricId: string): number
//  VisualAgg(MetricId: string): number
//
const metricFunctions: { [funcId: string]: IMetricFunctionSpec } = {
    "VisualAvg": { name: "VisualAvg", metricArgIdxs: [0, 1], bFilterScope: true },
    "VisualMin": { name: "VisualMin", metricArgIdxs: [0], bFilterScope: true },
    "VisualMax": { name: "VisualMax", metricArgIdxs: [0], bFilterScope: true },
    "VisualAgg": { name: "VisualMax", metricArgIdxs: [0], bFilterScope: true },
};

export interface IDataSetAvgOptions {
    weightByMetricId?: string,
    divZeroDefault?: number,
    fallBackToNoWeight?: boolean,
    positiveOnly?: boolean
}


export class DataSetUtils {

    static Attribute_format(attr: IdsAttribute, value: any, dctNodes: { [id: number]: IdsNode}, bIncludeNodeId?: boolean, bNodeIdOnly?: boolean): string {
        switch (attr?.Type) {
            case "string":
                return value || ""

            case "number":
                if (_.isString(attr.numberFormat) && !_.isEmpty(attr.numberFormat))
                    return NumberUtils.msformat(attr.numberFormat, +value)
                else
                    return NumberUtils.msformat("#,##0.00", +value)

            case "node": {
                const node = dctNodes?.[<number>+value]
                if (_.isEmpty(node))
                    return "Unknown (" + value + ")"
                else if (bIncludeNodeId)
                    return `${node.name} (${node.id_ui})`
                else if (bNodeIdOnly)
                    return `${node.id_ui}`
                else
                    return node.name
            }

            case "date": {
                if (_.isDate(value)) {
                    return DateUtils.formatDate(value)
                } else if (_.isString(value)) {
                    return value
                } else {
                    return "-"
                }
            }

            case "sapdate":
                if (_.isEmpty(value)) {
                    return "-"
                } else {
                    return DateUtils.formatDate(DateUtils.convertFromSAPBW(value))
                }

            default:
                return ""
        }
    }

    static Metric_format(mtrc: IdsMetric, value: any, currencySymbol?: string): string {
        let retVal = ""
        if (!_.isEmpty(mtrc?.format))
            retVal = NumberUtils.msformat(mtrc.format, +value)
        else
            retVal = NumberUtils.msformat("#,##0.00", value)
        if (mtrc && ("currency" in mtrc) && mtrc.currency && !_.isEmpty(currencySymbol))
            retVal = currencySymbol + retVal
        return retVal
    }

    static Attribute_getIndexingValue(attr: IdsAttribute, value: any): string {
        //--
        switch (attr?.Type) {

            case "string":
                return value || ""

            case "number":
                return "" + value || ""

            case "node": {
                return "" + value || ""
            }

            case "date": {
                if (_.isDate(value)) {
                    return DateUtils.convertToSAPBW(value)
                } else if (_.isString(value)) {
                    return value
                } else {
                    return "-"
                }
            }

            case "sapdate":
                return value || ""

            default:
                return ""
        }

    }

    static Attribute_reverseIndexingValue(attr: IdsAttribute, idxValue: string): any {
        //--
        switch (attr?.Type) {

            case "string":
                return idxValue

            case "number":
                return +idxValue

            case "node": {
                return +idxValue
            }

            case "date": {
                if (idxValue == "-")
                    return undefined

                return DateUtils.convertFromSAPBW(idxValue)
            }

            case "sapdate":
                return idxValue || ""

            default:
                return idxValue
        }

    }

    static Attribute_isDateLike(attr: IdsAttribute): boolean {
        switch (attr?.Type) {
            case "sapdate":
            case "date":
                return true
        }
        return false
    }

    static Attribute_getDate(attr: IdsAttribute, value: any): Date {
        switch (attr?.Type) {
            case "sapdate":
                return DateUtils.convertFromSAPBW(value)
            case "date":
                if(_.isDate(value))
                    return value
                // Should we try to convert from ISO string ?
                return undefined
        }
        return undefined
    }

    static Metric_setAvg(dataSet: IMetricDataSet, metricId: string, tgtKey: string, rows: IdsRow[], options?: IDataSetAvgOptions): number {
        if (_.isEmpty(dataSet) || _.isEmpty(metricId) || _.isEmpty(rows))
            return;
        const srcMetric = dataSet.getMetric(metricId);
        if (_.isEmpty(srcMetric))
            return
        let wgtMetric: IdsMetric = undefined
        if (!_.isEmpty(options?.weightByMetricId)) {
            wgtMetric = dataSet.getMetric(options?.weightByMetricId);
        }
        const bPositiveOnly = !!options?.positiveOnly
        let numTotal = 0
        let denTotal = 0
        let numTotal_NotWeighted = 0
        let denTotal_NotWeighted = 0

        _.forEach(rows, (row) => {
            const mtrValue = dataSet.getRowMetric(row, metricId) || 0
            if (bPositiveOnly && (!(mtrValue > 0)))
                return

            numTotal_NotWeighted += mtrValue
            denTotal_NotWeighted += 1

            if (wgtMetric) {
                const wgtValue = dataSet.getRowMetric(row, options?.weightByMetricId) || 0
                numTotal += mtrValue * wgtValue
                denTotal += wgtValue
            } else {
                numTotal += mtrValue
                denTotal += 1
            }
        })

        let avgVal = 0
        if ((wgtMetric && (denTotal == 0)) && options?.fallBackToNoWeight) {
            avgVal = numTotal_NotWeighted / denTotal_NotWeighted
        } else {
            avgVal = numTotal / denTotal
        }

        if (!_.isEmpty(tgtKey))
            _.forEach(rows, (row) => {
                dataSet.setRowData(row, tgtKey, avgVal)
            })

        return avgVal

    }

    static Attribute_setAvg(dataSet: IMetricDataSet, attributeId: string, tgtKey: string, rows: IdsRow[], options?: IDataSetAvgOptions): number {
        if (_.isEmpty(dataSet) || _.isEmpty(attributeId) || _.isEmpty(rows))
            return;
        const srcAttribute = dataSet.getAttribute(attributeId);
        if (_.isEmpty(srcAttribute))
            return
        if (srcAttribute.Type != "number")
            return

        let wgtMetric: IdsMetric = undefined
        if (!_.isEmpty(options?.weightByMetricId)) {
            wgtMetric = dataSet.getMetric(options?.weightByMetricId);
        }
        const bPositiveOnly = !!options?.positiveOnly
        let numTotal = 0
        let denTotal = 0
        let numTotal_NotWeighted = 0
        let denTotal_NotWeighted = 0

        _.forEach(rows, (row) => {
            const atrValue = dataSet.getRowAttribute(row, attributeId) || 0
            if (bPositiveOnly && (!(atrValue > 0)))
                return

            numTotal_NotWeighted += atrValue
            denTotal_NotWeighted += 1

            if (wgtMetric) {
                const wgtValue = dataSet.getRowMetric(row, options?.weightByMetricId) || 0
                numTotal += atrValue * wgtValue
                denTotal += wgtValue
            } else {
                numTotal += atrValue
                denTotal += 1
            }
        })

        let avgVal = 0
        if ((wgtMetric && (denTotal == 0)) && options?.fallBackToNoWeight) {
            avgVal = numTotal_NotWeighted / denTotal_NotWeighted
        } else {
            avgVal = numTotal / denTotal
        }

        if (!_.isEmpty(tgtKey))
            _.forEach(rows, (row) => {
                dataSet.setRowData(row, tgtKey, avgVal)
            })

        return avgVal

    }

    static Metric_setMin(dataSet: IMetricDataSet, metricId: string, tgtKey: string, rows: IdsRow[], options?: {
        bPositiveOnly?: boolean
    }): number {
        if (_.isEmpty(dataSet) || _.isEmpty(metricId) || _.isEmpty(rows))
            return;

        const srcMetric = dataSet.getMetric(metricId);
        if (_.isEmpty(srcMetric))
            return;

        const bPositiveOnly = !!options?.bPositiveOnly
        let minValue: number = undefined
        _.forEach(rows, (row) => {
            const mtrValue = dataSet.getRowMetric(row, metricId) || 0
            if (bPositiveOnly && !(mtrValue > 0))
                return;
            minValue = Math.min(minValue || mtrValue, mtrValue);
        })

        if (_.isUndefined(minValue)) {
            minValue = 0
        }

        if (!_.isEmpty(tgtKey))
            _.forEach(rows, (row) => {
                dataSet.setRowData(row, tgtKey, minValue)
            })

        return minValue

    }

    static Metric_setSum(dataSet: IMetricDataSet, metricId: string, tgtKey: string, rows: IdsRow[], options?: {
        bPositiveOnly?: boolean
    }): number {
        if (_.isEmpty(dataSet) || _.isEmpty(metricId) || _.isEmpty(rows))
            return;

        const srcMetric = dataSet.getMetric(metricId);
        if (_.isEmpty(srcMetric))
            return;

        const bPositiveOnly = !!options?.bPositiveOnly
        let sumValue: number = 0
        _.forEach(rows, (row) => {
            const mtrValue = dataSet.getRowMetric(row, metricId) || 0
            if (bPositiveOnly && !(mtrValue > 0))
                return;
            sumValue += mtrValue
        })


        if (!_.isEmpty(tgtKey))
            _.forEach(rows, (row) => {
                dataSet.setRowData(row, tgtKey, sumValue)
            })

        return sumValue

    }

    static Metric_setAggregate(dataSet: IMetricDataSet, metricId: string, tgtKey: string, rows: IdsRow[]): number {
        if (_.isEmpty(dataSet) || _.isEmpty(metricId) || _.isEmpty(rows))
            return;

        const srcMetric = dataSet.getMetric(metricId);
        if (_.isEmpty(srcMetric))
            return;

        let aggValue: number = dataSet.getRowAggregateMetric(rows, metricId)

        if(_.isUndefined(aggValue))
            return DataSetUtils.Metric_setSum(dataSet, metricId, tgtKey, rows)

        if (!_.isEmpty(tgtKey))
            _.forEach(rows, (row) => {
                dataSet.setRowData(row, tgtKey, aggValue)
            })

        return aggValue

    }


    static Metric_setMax(dataSet: IMetricDataSet, metricId: string, tgtKey: string, rows: IdsRow[], options?: {
        bPositiveOnly?: boolean
    }): number {
        if (_.isEmpty(dataSet) || _.isEmpty(metricId) || _.isEmpty(rows))
            return;

        const srcMetric = dataSet.getMetric(metricId);
        if (_.isEmpty(srcMetric))
            return;

        const bPositiveOnly = !!options?.bPositiveOnly
        let maxValue: number = undefined
        _.forEach(rows, (row) => {
            const mtrValue = dataSet.getRowMetric(row, metricId) || 0
            if (bPositiveOnly && !(mtrValue > 0))
                return;
            maxValue = Math.max(maxValue || mtrValue, mtrValue);
        })

        if (_.isUndefined(maxValue)) {
            maxValue = 0
        }

        if (!_.isEmpty(tgtKey))
            _.forEach(rows, (row) => {
                dataSet.setRowData(row, tgtKey, maxValue)
            })

        return maxValue
    }

    static Function_getSpecs(functionId: string): IMetricFunctionSpec {
        return metricFunctions[functionId]
    }

    static Formula_setDependentParts(node: MathNode,
                                     dctSymbols: { [symbolId: string]: string },
                                     dctKnownFunction: { [funcId: string]: string },
                                     errors: string[]) {
        dctSymbols = dctSymbols || {}
        dctKnownFunction = dctKnownFunction || {}
        errors = errors || []

        node.traverse((node: (MathNode | SymbolNode | FunctionNode) & any, path, parent) => {
            if (node.type == "SymbolNode") {
                dctSymbols[node?.name] = node?.name;
            } else if (node.type == "FunctionNode") {
                // If it's a known function, then get the symbols from the arguments
                if (node?.name in metricFunctions) {
                    dctKnownFunction[node.name] = node.name
                    const n = <FunctionNode>node;
                    //Extract constant values strings as metric symbol references
                    for (const idx of metricFunctions[node.name].metricArgIdxs) {
                        const arg = n.args[idx];
                        if (_.isEmpty(arg))
                            continue;
                        if (arg.type == "ConstantNode") {
                            const cn = <ConstantNode>arg;
                            if (_.isString(cn.value)) {
                                dctSymbols[cn.value] = cn.value;
                            } else {
                                errors.push(`Function ${node.name} has non-string reference, argument ${idx + 1}, ${cn.value}`)
                            }
                        }
                    }
                }
            } else {
                //console.log("Node Type: ", node?.type);
            }
        });
        // Remove Symbols of function names
        _.forEach(_.values(dctKnownFunction), (fncName) => {
            if (fncName in dctSymbols)
                delete dctSymbols[fncName]
        })
    }

    static Symbols_getTypes(symbolIds: string[], dataSet: IMetricDataSet): IDataSetSymbolTypes {
        const rv: IDataSetSymbolTypes = {
            attributes: [],
            metrics: [],
            unknown: []
        }

        if (_.isEmpty(dataSet))
            return rv

        _.forEach(symbolIds, (symbolId) => {
            switch (dataSet.getSymbolType(symbolId)) {
                case "Metric":
                    rv.metrics.push(symbolId)
                    break
                case "Attribute":
                    rv.attributes.push(symbolId)
                    break
                default:
                    rv.unknown.push(symbolId)
            }
        })

        return rv
    }

    static Expression_createRowScope(attributeIds: string[], metricIds: string[], dataSet: IMetricDataSet, row: IdsRow): {
        [symbolId: string]: any
    } {
        const rv: any = {}
        if (_.isEmpty(dataSet) || _.isEmpty(row))
            return rv

        _.forEach(attributeIds || [], (attributeId) => {
            const atrValue = dataSet.getRowAttribute(row, attributeId)
            rv[attributeId] = atrValue
        })
        _.forEach(metricIds || [], (metricId) => {
            const mtrValue = dataSet.getRowMetric(row, metricId)
            rv[metricId] = mtrValue
        })

        return rv
    }

    static DataSet_summarize(dataSet: IMetricDataSet): IDataSetSummary {
        if (_.isEmpty(dataSet))
            return undefined

        const rv: IDataSetSummary = {
            name: dataSet?.Name || "Not Set",
            dataProvider: {
                name: dataSet?.DataProvider?.Name || "Not Set"
            },
            status: {
                loaded: !!dataSet?.bLoaded,
                dataLoadingPaused: !!dataSet?.DataLoadingPaused,
                rowCountThreshold: dataSet?.RowCountThreshold,

                structureLoaded: {
                    attributes: _.map(dataSet.getAttributes(true), (a) => a.Id),
                    metrics: _.map(dataSet.getMetrics(true), (a) => a.Id),
                    filters: undefined,

                    clientSideRowCount: dataSet.AllRows.length,
                    filteredRowCount: dataSet.FilteredRows.length
                },

                structureQueued: {
                    attributes: _.map(dataSet.getAttributes(false), (a) => a.Id),
                    metrics: _.map(dataSet.getMetrics(false), (a) => a.Id),
                    filters: undefined
                }
            },
            schema: {
                attributes: [],
                metrics: [],
                hiddenMetrics: []
            }
        }

        // Add Filters
        function summarizeFilters(bLoaded: boolean): IDataSetFilterSummary {
            const s: IDataSetFilterSummary = {
                filterCount: 0
            }
            _.forEach(dataSet.getAllFilters(bLoaded), (fs: IdsFilterSpec) => {
                s.filterCount++

                switch (fs.type) {
                    case "static":
                        s.staticCount = s.staticCount || 0
                        s.staticFilterIds = s.staticFilterIds || []
                        s.staticCount++
                        s.staticFilterIds.push(fs.id)
                        break;
                    case "client-side":
                        s.clientSideCount = s.clientSideCount || 0
                        s.clientSideFilterIds = s.clientSideFilterIds || []
                        s.clientSideCount++
                        s.clientSideFilterIds.push(fs.id)
                        break;
                    case "server-side":
                        s.serverSideCount = s.serverSideCount || 0
                        s.serverSideFilterIds = s.serverSideFilterIds || []
                        s.serverSideCount++
                        s.serverSideFilterIds.push(fs.id)
                        break;
                }
            })

            return s
        }

        rv.status.structureLoaded.filters = summarizeFilters(true)
        rv.status.structureQueued.filters = summarizeFilters(false)

        // Add Schema Attributes
        _.forEach(dataSet.getAvailableAttributes(), (atr) => {
            const atrClone: IdsAttribute & any = {
                Id: atr.Id,
                Name: atr.Name,
                ShortName: atr.ShortName,
                Type: atr.Type,
                SortOrder: atr.SortOrder
            }

            if (!_.isUndefined(atr.MetaType))
                atrClone.MetaType = atr.MetaType

            if (!_.isUndefined(atr.EntityTypeId))
                atrClone.EntityTypeId = atr.EntityTypeId

            if (!_.isUndefined(atr.numberFormat))
                atrClone.numberFormat = atr.numberFormat

            if (atr.bAttributeComputed) {
                atrClone.bAttributeComputed = true
                if (!_.isEmpty(atr.requiredAttributeIds))
                    atrClone.requiredAttributeIds = _.clone(atr.requiredAttributeIds)
                if (!_.isEmpty(atr.requiredMetricsIds))
                    atrClone.requiredMetricsIds = _.clone(atr.requiredMetricsIds)
            }

            rv.schema.attributes.push(atrClone)
        })

        // Add Schema Metrics
        _.forEach(dataSet.getAvailableMetrics(), (mtr) => {
            const mtrClone: IdsMetric & any = {
                Id: mtr.Id,
                Name: mtr.Name,
                ShortName: mtr.ShortName,
                SortOrder: mtr.SortOrder
            }
            if (!_.isEmpty(mtr.format))
                mtrClone.format = mtr.format
            if (!_.isEmpty(mtr.axisType))
                mtrClone.axisType = mtr.axisType

            if (mtr.bMetricComputed) {
                mtrClone.bMetricComputed = true
                if (!_.isEmpty(mtr.requiredAttributeIds))
                    mtrClone.requiredAttributeIds = _.clone(mtr.requiredAttributeIds)
                if (!_.isEmpty(mtr.requiredMetricsIds))
                    mtrClone.requiredMetricsIds = _.clone(mtr.requiredMetricsIds)
            }

            if (mtr.Hidden) {
                mtrClone.Hidden = true
                rv.schema.hiddenMetrics.push(mtrClone)
            } else {
                rv.schema.metrics.push(mtrClone)
            }

        })

        return rv
    }

    static DataSet_createMessages(dataSet: IMetricDataSet | IMetricDataSet[]): IMessage[] {
        const rv: IMessage[] = []

        if (_.isEmpty(dataSet))
            return []

        if (!_.isArray(dataSet))
            dataSet = [dataSet]

        _.forEach(dataSet, (ds: IMetricDataSet, idx) => {

            if (_.isEmpty(ds))
                return

            const summary = DataSetUtils.DataSet_summarize(ds)
            const summaryString = JSON.stringify(summary, undefined, "\t")

            const msg: IMessage = {
                code: `DataSet-${idx}`,
                type: MessageType.Information,
                title: `DataSet: ${ds.Name}`,
                subtitle: ds.Name,
                // unfortunately this is too big to render in MessageViewItem/ MessageItem
                description: summaryString,
                details: summary,
                count: 0
            }

            rv.push(msg)


        })

        return rv
    }
}